import { useEffect, useRef, useState } from 'react';

const LatticeSVGFallback = () => (
  <svg viewBox="0 0 1200 800" className="w-full h-full" preserveAspectRatio="xMidYMid slice" aria-hidden="true">
    <defs>
      <pattern id="lattice-grid" width="60" height="60" patternUnits="userSpaceOnUse">
        <path d="M 60 0 L 0 0 0 60" fill="none" stroke="rgba(0,255,65,0.07)" strokeWidth="0.5" />
      </pattern>
      <radialGradient id="lattice-glow" cx="30%" cy="40%">
        <stop offset="0%" stopColor="rgba(0,255,65,0.12)" />
        <stop offset="60%" stopColor="rgba(212,160,23,0.04)" />
        <stop offset="100%" stopColor="transparent" />
      </radialGradient>
    </defs>
    <rect width="100%" height="100%" fill="url(#lattice-grid)" />
    <circle cx="360" cy="320" r="420" fill="url(#lattice-glow)" />
    {[
      {x:150,y:180},{x:380,y:120},{x:600,y:220},{x:320,y:380},{x:540,y:360},
      {x:160,y:500},{x:740,y:280},{x:480,y:540},{x:900,y:180},{x:850,y:430},
      {x:1050,y:300},{x:700,y:500},{x:250,y:650},{x:950,y:550},{x:1100,y:200},
    ].map((p, i, arr) => (
      <g key={i}>
        <circle cx={p.x} cy={p.y} r="2.5" fill="#00FF41" opacity="0.5">
          <animate attributeName="opacity" values="0.3;0.7;0.3" dur={`${3 + (i % 4)}s`} repeatCount="indefinite" />
        </circle>
        {i > 0 && (
          <line
            x1={p.x} y1={p.y}
            x2={arr[(i + 3) % arr.length].x} y2={arr[(i + 3) % arr.length].y}
            stroke="#00FF41" strokeWidth="0.4" opacity="0.1"
          >
            <animate attributeName="opacity" values="0.05;0.15;0.05" dur={`${4 + (i % 3)}s`} repeatCount="indefinite" />
          </line>
        )}
        {i % 3 === 0 && (
          <line
            x1={p.x} y1={p.y}
            x2={arr[(i + 7) % arr.length].x} y2={arr[(i + 7) % arr.length].y}
            stroke="#D4A017" strokeWidth="0.3" opacity="0.06"
          />
        )}
      </g>
    ))}
  </svg>
);

const HeroBackground = () => {
  const canvasRef = useRef(null);
  const [webglFailed, setWebglFailed] = useState(false);
  const [webglReady, setWebglReady] = useState(false);
  const animRef = useRef(null);

  useEffect(() => {
    // Respect reduced motion
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setWebglFailed(true);
      return;
    }

    const canvas = canvasRef.current;
    if (!canvas) { setWebglFailed(true); return; }

    // Timeout: if WebGL doesn't init in 3s, fall back
    const timeout = setTimeout(() => {
      if (!webglReady) setWebglFailed(true);
    }, 3000);

    try {
      const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
      if (!gl) { setWebglFailed(true); clearTimeout(timeout); return; }

      const w = canvas.width = canvas.clientWidth;
      const h = canvas.height = canvas.clientHeight;
      gl.viewport(0, 0, w, h);

      // Simple particle system — lattice nodes
      const NUM = 60;
      const positions = [];
      for (let i = 0; i < NUM; i++) {
        positions.push(
          (Math.random() * 2 - 1),
          (Math.random() * 2 - 1),
          Math.random() * 0.5 + 0.2, // speed
          Math.random() * Math.PI * 2, // phase
        );
      }

      const vsrc = `
        attribute vec2 a_pos;
        uniform float u_time;
        uniform float u_aspect;
        attribute float a_speed;
        attribute float a_phase;
        varying float v_alpha;
        void main() {
          float t = u_time * a_speed * 0.3;
          vec2 p = a_pos + vec2(sin(t + a_phase) * 0.02, cos(t * 0.7 + a_phase) * 0.015);
          gl_Position = vec4(p.x / u_aspect, p.y, 0.0, 1.0);
          gl_PointSize = 3.0;
          v_alpha = 0.3 + 0.3 * sin(t + a_phase);
        }
      `;
      const fsrc = `
        precision mediump float;
        varying float v_alpha;
        void main() {
          float d = length(gl_PointCoord - 0.5) * 2.0;
          if (d > 1.0) discard;
          float a = (1.0 - d * d) * v_alpha;
          gl_FragColor = vec4(0.0, 1.0, 0.255, a);
        }
      `;

      const compile = (type, src) => {
        const s = gl.createShader(type);
        gl.shaderSource(s, src);
        gl.compileShader(s);
        if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s));
        return s;
      };

      const prog = gl.createProgram();
      gl.attachShader(prog, compile(gl.VERTEX_SHADER, vsrc));
      gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, fsrc));
      gl.linkProgram(prog);
      if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link failed');
      gl.useProgram(prog);

      const posData = new Float32Array(NUM * 2);
      const speedData = new Float32Array(NUM);
      const phaseData = new Float32Array(NUM);
      for (let i = 0; i < NUM; i++) {
        posData[i * 2] = positions[i * 4];
        posData[i * 2 + 1] = positions[i * 4 + 1];
        speedData[i] = positions[i * 4 + 2];
        phaseData[i] = positions[i * 4 + 3];
      }

      const posB = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, posB);
      gl.bufferData(gl.ARRAY_BUFFER, posData, gl.STATIC_DRAW);
      const posL = gl.getAttribLocation(prog, 'a_pos');
      gl.enableVertexAttribArray(posL);
      gl.vertexAttribPointer(posL, 2, gl.FLOAT, false, 0, 0);

      const speedB = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, speedB);
      gl.bufferData(gl.ARRAY_BUFFER, speedData, gl.STATIC_DRAW);
      const speedL = gl.getAttribLocation(prog, 'a_speed');
      gl.enableVertexAttribArray(speedL);
      gl.vertexAttribPointer(speedL, 1, gl.FLOAT, false, 0, 0);

      const phaseB = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, phaseB);
      gl.bufferData(gl.ARRAY_BUFFER, phaseData, gl.STATIC_DRAW);
      const phaseL = gl.getAttribLocation(prog, 'a_phase');
      gl.enableVertexAttribArray(phaseL);
      gl.vertexAttribPointer(phaseL, 1, gl.FLOAT, false, 0, 0);

      const uTime = gl.getUniformLocation(prog, 'u_time');
      const uAspect = gl.getUniformLocation(prog, 'u_aspect');
      gl.uniform1f(uAspect, w / h);

      gl.enable(gl.BLEND);
      gl.blendFunc(gl.SRC_ALPHA, gl.ONE);

      setWebglReady(true);
      clearTimeout(timeout);

      const render = (t) => {
        gl.clearColor(0.02, 0.043, 0.078, 0);
        gl.clear(gl.COLOR_BUFFER_BIT);
        gl.uniform1f(uTime, t * 0.001);
        gl.drawArrays(gl.POINTS, 0, NUM);
        animRef.current = requestAnimationFrame(render);
      };
      animRef.current = requestAnimationFrame(render);
    } catch {
      setWebglFailed(true);
      clearTimeout(timeout);
    }

    return () => {
      clearTimeout(timeout);
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, []);

  return (
    <div className="absolute inset-0 opacity-30" aria-hidden="true">
      {/* SVG always renders first — instant, no blank screen */}
      {(!webglReady || webglFailed) && <LatticeSVGFallback />}
      {/* WebGL canvas overlays when ready */}
      {!webglFailed && (
        <canvas
          ref={canvasRef}
          className={`absolute inset-0 w-full h-full transition-opacity duration-slow ${webglReady ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

export default HeroBackground;
